import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useCartStore } from '@/stores/cartStore'
import { useClickOutside } from '@/hooks/useClickOutside'
import { formatPrice } from '@/lib/utils'
import { Minus, Plus, X, Gift, Truck } from 'lucide-react'

export function CartDrawer() {
  const {
    items,
    isCartOpen,
    toggleCart,
    updateQuantity,
    removeItem,
    giftWrap,
    toggleGiftWrap,
    subtotal,
    total,
  } = useCartStore()

  const panelRef = useRef<HTMLElement>(null)
  useClickOutside(panelRef, () => toggleCart(false), isCartOpen)

  return (
    <AnimatePresence>
      {isCartOpen && (
        <motion.div
          key="cart-wrapper"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 1 }}
          className="fixed inset-0 z-[60]"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => toggleCart(false)}
            className="absolute inset-0 bg-ink/20 backdrop-blur-sm"
          />
          <motion.aside
            ref={panelRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.35 }}
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-paper shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-ink/10 px-6 py-5">
              <h2 className="font-display text-xl normal-case">your bag</h2>
              <button
                onClick={() => toggleCart(false)}
                aria-label="Close cart"
                className="text-carbon/50 hover:text-wd-orange"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
                <p className="mb-4 text-lg text-carbon">Your bag is empty.</p>
                <Link
                  to="/shop"
                  onClick={() => toggleCart(false)}
                  className="text-sm text-carbon hover:underline"
                >
                  Explore the collection
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-6">
                  <div className="space-y-6">
                    {items.map((item) => (
                      <div key={item.id} className="flex gap-4">
                        <img
                          src={item.image}
                          alt={item.perfumeName}
                          className="h-24 w-20 object-cover"
                        />
                        <div className="flex flex-1 flex-col justify-between">
                          <div>
                            <Link
                              to={`/product/${item.perfumeId}`}
                              onClick={() => toggleCart(false)}
                              className="font-display text-carbon hover:text-wd-orange"
                            >
                              {item.perfumeName.toLowerCase()}
                            </Link>
                            <p className="text-xs text-carbon/50">{item.sizeLabel}</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center border border-ink/10">
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                className="px-2 py-1 text-carbon hover:text-wd-orange"
                                aria-label="Decrease quantity"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="px-2 text-sm text-carbon">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                className="px-2 py-1 text-carbon hover:text-wd-orange"
                                aria-label="Increase quantity"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                            <span className="text-sm text-carbon">{formatPrice(item.price * item.quantity)}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="self-start text-carbon/30 hover:text-wd-orange"
                          aria-label={`Remove ${item.perfumeName}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={toggleGiftWrap}
                    className="mt-6 flex w-full items-center justify-between border border-ink/10 p-4 text-left transition-colors hover:border-ink/30"
                  >
                    <div className="flex items-center gap-3">
                      <Gift className="h-4 w-4 text-wd-orange" />
                      <div>
                        <p className="text-sm text-carbon">Add gift wrap</p>
                        <p className="text-xs text-carbon/50">+{formatPrice(2500)} per order</p>
                      </div>
                    </div>
                    <div
                      className={`h-4 w-4 rounded-full border ${
                        giftWrap ? 'border-wd-orange bg-wd-orange' : 'border-ink/20'
                      }`}
                    />
                  </button>
                </div>

                <div className="border-t border-ink/10 px-6 py-6">
                  <div className="mb-4 flex items-start gap-2 text-xs text-carbon/50">
                    <Truck className="h-4 w-4 shrink-0" />
                    <span>Free shipping on orders over ₦30,000.</span>
                  </div>
                  <div className="mb-4 flex justify-between text-sm text-carbon/60">
                    <span>Subtotal</span>
                    <span>{formatPrice(subtotal())}</span>
                  </div>
                  {giftWrap && (
                    <div className="mb-2 flex justify-between text-sm text-carbon/60">
                      <span>Gift wrap</span>
                      <span>{formatPrice(2500)}</span>
                    </div>
                  )}
                  <div className="mb-6 flex justify-between text-lg text-carbon">
                    <span className="font-display">Total</span>
                    <span className="font-display">{formatPrice(total())}</span>
                  </div>
                  <Link
                    to="/checkout"
                    onClick={() => toggleCart(false)}
                    className="block w-full bg-ink py-4 text-center text-xs font-medium normal-case tracking-[0.12em] text-white transition-colors hover:bg-wd-orange"
                  >
                    Checkout
                  </Link>
                </div>
              </>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
